import { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";

export default function AdminLogin() {
  const { user, loading, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  if (loading) return null;
  if (user && user.role === "admin") return <Navigate to="/admin" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      await login(email, password);
      navigate("/admin");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[var(--rciq-navy)] text-white flex items-center justify-center px-6 relative overflow-hidden" data-testid="admin-login-page">
      <div className="absolute inset-0 bg-dot-pattern opacity-50" />
      <div className="absolute -top-40 -left-40 w-[500px] h-[500px] rounded-full bg-[var(--rciq-teal)] opacity-[0.08] blur-3xl" />
      <div className="relative w-full max-w-md">
        <a href="/" className="font-serif-display text-2xl font-700 inline-block">Revenue Cycle IQ</a>
        <div className="text-[10px] tracking-[0.22em] uppercase text-[var(--rciq-teal)] font-700 mt-1">
          Admin · Insights CMS
        </div>

        <form onSubmit={submit} className="mt-10 bg-white/[0.04] border border-white/10 rounded-2xl p-8 backdrop-blur" data-testid="admin-login-form">
          <h1 className="font-serif-display text-3xl font-700">Sign in</h1>
          <p className="mt-2 text-sm text-white/60">Manage articles, drafts, and subscribers.</p>

          <div className="mt-8 space-y-4">
            <div>
              <label className="block text-xs uppercase tracking-[0.12em] text-white/60 font-700 mb-2">Email</label>
              <input
                data-testid="login-email"
                type="email" required
                value={email} onChange={(e) => setEmail(e.target.value)}
                className="w-full bg-white/5 border border-white/15 rounded-lg px-4 py-3 text-sm focus:outline-none focus:border-[var(--rciq-teal)]"
                placeholder="you@therevenuecycleiq.com"
              />
            </div>
            <div>
              <label className="block text-xs uppercase tracking-[0.12em] text-white/60 font-700 mb-2">Password</label>
              <input
                data-testid="login-password"
                type="password" required
                value={password} onChange={(e) => setPassword(e.target.value)}
                className="w-full bg-white/5 border border-white/15 rounded-lg px-4 py-3 text-sm focus:outline-none focus:border-[var(--rciq-teal)]"
              />
            </div>
          </div>

          {error && <div data-testid="login-error" className="mt-4 text-sm text-red-300 bg-red-500/10 border border-red-400/20 rounded-lg p-3">{error}</div>}

          <button
            data-testid="login-submit"
            type="submit"
            disabled={submitting}
            className="mt-6 w-full btn-pill btn-pill-primary justify-center text-[12px]"
          >
            {submitting ? "Signing in..." : "Sign in"}
          </button>
        </form>
      </div>
    </div>
  );
}
